/**
 * AdminPromoRequests — sticker program: free promo materials to mail (1 Oct 2026).
 * Advertisers request stickers with one click (sign-up or Free Program tab); each request lands here with the
 * mailing address. Mark it "sent" after posting. A request whose address was already used by another account is
 * flagged (the free 3 months are once per company).
 */
import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase-config';
import '../portal/portal-theme.css';

const fmt = (t) => (t?.toDate ? t.toDate().toLocaleDateString() : '');
const lines = (a = {}) => [a.name, a.street, a.street2, `${a.postalCode || ''} ${a.city || ''}${a.state ? `, ${a.state}` : ''}`.trim(), a.country].filter(Boolean);

const AdminPromoRequests = ({ user }) => {
  const [rows, setRows] = useState(null);
  const [show, setShow] = useState('requested');
  const [msg, setMsg] = useState('');

  const load = async () => {
    setRows(null);
    const snap = await getDocs(query(collection(db, 'users'), where('promoRequest.status', '==', show)));
    const list = [];
    for (const d of snap.docs) {
      const u = { id: d.id, ...d.data() };
      // another account with the same mailing address?
      let dup = [];
      if (u.promoRequest?.addressKey) {
        const same = await getDocs(query(collection(db, 'users'), where('promoRequest.addressKey', '==', u.promoRequest.addressKey)));
        dup = same.docs.filter((x) => x.id !== d.id).map((x) => x.data().companyName || x.data().email);
      }
      list.push({ ...u, dup });
    }
    setRows(list);
  };
  useEffect(() => { load(); }, [show]);

  const markSent = async (u) => {
    await updateDoc(doc(db, 'users', u.id), {
      'promoRequest.status': 'sent', 'promoRequest.sentAt': Timestamp.now(), 'promoRequest.sentBy': user?.email || 'admin',
    });
    setMsg(`Marked as sent: ${u.companyName || u.email}`);
    load();
  };
  const copy = (u) => { navigator.clipboard?.writeText(lines(u.promoRequest.address).join('\n')); setMsg('Address copied.'); };

  return (
    <div style={{ maxWidth: 860 }}>
      <h2 className="ct-title">📦 Promo requests</h2>
      <p className="ct-muted">Free stickers / flyers to mail. Copy the address, post the materials, then mark it sent.</p>
      <div className="ct-row" style={{ margin: '14px 0' }}>
        <button className={`ct-btn ${show === 'requested' ? 'on' : ''}`} onClick={() => setShow('requested')}>To send</button>
        <button className={`ct-btn ${show === 'sent' ? 'on' : ''}`} onClick={() => setShow('sent')}>Sent</button>
        <button className="ct-btn" onClick={load}>🔄 Refresh</button>
      </div>
      {msg && <p className="ct-msg">{msg}</p>}
      {rows === null ? <p className="ct-muted">Loading…</p> : rows.length === 0 ? <div className="ct-card"><p className="ct-muted" style={{ margin: 0 }}>{show === 'requested' ? '✅ Nothing to send.' : 'None sent yet.'}</p></div> : rows.map((u) => (
        <div key={u.id} className="ct-card">
          <h3>{u.companyName || 'Unnamed company'} <span className="ct-muted">· {u.email}</span></h3>
          <div className="ct-muted">
            Requested {fmt(u.promoRequest.requestedAt)}{u.promoRequest.sentAt ? ` · sent ${fmt(u.promoRequest.sentAt)}` : ''}
            {u.stickerProgramUsedAt ? ' · free months already used' : ''}
          </div>
          <div className="ct-address">{lines(u.promoRequest.address).join('\n')}</div>
          {u.promoRequest.note && <p className="ct-muted">Note: {u.promoRequest.note}</p>}
          {u.dup.length > 0 && <p className="ct-warn">⚠️ Same address as: {u.dup.join(', ')} — the free program is once per company.</p>}
          <div className="ct-row">
            <button className="ct-btn" onClick={() => copy(u)}>📋 Copy address</button>
            {show === 'requested' && <button className="ct-btn green" onClick={() => markSent(u)}>✅ Mark as sent</button>}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminPromoRequests;
